import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import RemarkDialog from "@components/dialog/remarkDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";

import { formatDateTime } from "@utils/formatter";

const ACTION = {
  UPDATE: 'Update',
  COLLECTED: 'Collected',
  RESET: 'Reset',
  DECLINED: 'Declined'
}

const schema = z.object({
  remarks: z.string().min(1, { message: 'Remark is required' }),
})

export default function FeaturedPaymentForm() {
  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication()
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const { handleSubmit, control, getValues, reset, watch, setValue } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      remarks: "",
    },
  });

  const [paymentData, setPaymentData] = useState(null)

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmDialogAction, setConfirmDialogAction] = useState("")

  useEffect(()=>{
    if (((pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchPaymentMutation.mutate(id)
    }
  },[pageAction])

  const fetchPaymentMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getFeaturedDonorByGuid}/${id}`)
    },
    onSuccess: (res) => {
      const data = res.data
      data.collectedBy = data.collectedBy || '-'
      data.collectedOn = data.collectedOn ? formatDateTime(data.collectedOn) : '-'
      data.rejectedBy = data.rejectedBy || '-'
      data.rejectedOn = data.rejectedOn ? formatDateTime(data.rejectedOn) : '-'
      data.createdOn = data.createdOn ? formatDateTime(data.createdOn) : '-'
      data.remarks = data.remarks || ''
      setPaymentData(data)
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve payment", "error");
    }
  });

  const updateRemarkMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFeaturedDonorRemark}/${id}`, data)
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeaturedDonor'] })
      openSnackbar("Successfully update remark", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update remark", "error");
    }
  })

  const collectPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.collectFeaturedDonor}/${id}`, { updatedBy: user?.username })
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeaturedDonor'] })
      openSnackbar("Successfully changed status to collected", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to change status", "error");
    }
  })

  const rejectPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.rejectFeaturedDonor}/${id}`, { updatedBy: user?.username })
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeaturedDonor'] })
      openSnackbar("Successfully changed status to rejected", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to change status", "error");
    }
  })

  const resetPaymentMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.resetFeaturedDonor}/${id}`, { updatedBy: user?.username })
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getFeaturedDonor'] })
      openSnackbar("Successfully changed status to new", "success");
      fetchPaymentMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to change status", "error");
    }
  })

  const onSubmit = () => {
    setConfirmDialogAction(ACTION.UPDATE)
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();

    if(confirmDialogAction === ACTION.COLLECTED){
      collectPaymentMutation.mutate()
    }
    else if(confirmDialogAction === ACTION.DECLINED){
      rejectPaymentMutation.mutate()
    }
    else if(confirmDialogAction === ACTION.RESET){
      resetPaymentMutation.mutate()
    }
    else if(confirmDialogAction === ACTION.UPDATE){
      const formData = {
        remark: data?.remarks
      }
      updateRemarkMutation.mutate(formData)
    }

  }

  const onBack = () => {
    navigate('/admin/featured-donation', { replace: true });
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchPaymentMutation.isPending && !fetchPaymentMutation.isError && ["view"].includes(pageAction)) && isCurrentModuleAdmin &&
          <div style={{display: 'flex', gap: "10px"}}>
            <CustomButton color="#4DB6AC" disabled={paymentData?.status !== "New"} onClick={()=>{setConfirmDialogAction(ACTION.COLLECTED);setOpenConfirmDialog(true)}}>
              Collected
            </CustomButton>
            <CustomButton color="#D13F58" disabled={paymentData?.status !== "New"} onClick={()=>{setConfirmDialogAction(ACTION.DECLINED);setOpenConfirmDialog(true)}}>
              Declined
            </CustomButton>
            <CustomButton color="#FFD044" disabled={paymentData?.status === "New"} onClick={()=>{setConfirmDialogAction(ACTION.RESET);setOpenConfirmDialog(true)}}>
              Reset
            </CustomButton>
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchPaymentMutation.isPending} isError={fetchPaymentMutation.isError} errorMessage="Unable to load payment" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Donor Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="donorName"
                control={control}
                label="Donor Name"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="contactName"
                control={control}
                label="Contact Person Name"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="contactNumber"
                control={control}
                label="Contact Number"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="contactEmail"
                control={control}
                label="Contact Email"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="contactAddress"
                control={control}
                label="Contact Mailing Address"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Donation Details</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="featuredName"
                control={control}
                label="Featured Donation Name"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12} >
              <ControlledTextField 
                name="remarks"
                control={control}
                label="Remark"
                disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}
                multiline
                rows={3}
              />
            </Grid2>

            <Grid2 size={12} display={'flex'} justifyContent={'flex-end'}>
              <CustomButton type="submit" disabled={fetchPaymentMutation.isPending || fetchPaymentMutation.isError}>Update Remark</CustomButton>
            </Grid2>
          </Grid2>
        </Paper>


        {["view"].includes(pageAction) && <>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Other Details</Typography>
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="paymentNo"
                control={control}
                label="Payment No"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="status"
                control={control}
                label="Payment Status"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Submitted On"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="collectedBy"
                control={control}
                label="Collected By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="collectedOn"
                control={control}
                label="Collected On"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="rejectedBy"
                control={control}
                label="Declined By"
                readOnly={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="rejectedOn"
                control={control}
                label="Declined On"
                readOnly={true}
              />
            </Grid2>

          </Grid2>
        </Paper>
        </>}
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle={`Confirmation`}
        dialogDescription={`${
          confirmDialogAction === ACTION.COLLECTED ? 'Change status to Collected?' 
          : confirmDialogAction === ACTION.DECLINED ? 'Change status to Rejected?'
          : confirmDialogAction === ACTION.RESET ? 'Revert status to New?'
        : confirmDialogAction === ACTION.UPDATE ? 'Update remark?' : ''}`}
        onSubmit={()=>onConfirmSubmit()}
        loading={collectPaymentMutation.isPending || rejectPaymentMutation.isPending}
      />
    </div>
  );
}
